import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Filter, Search, X, Pencil } from 'lucide-react';
import EnhancedSuppliersTable from './EnhancedSuppliersTable';
import { TableColumn, ColumnWidth } from '../../../components/common/EnhancedTable/types';
import './EnhancedSuppliers.css';
import { supabase } from '../../../lib/supabase';
import { usePermissions } from '../../../hooks/usePermissions';
import SupplierFormModal from '../../../components/Suppliers/SupplierFormModal';
import type { Supplier, SortField } from '../../../types/supplier';

// Define the sort config type
type SortConfig = {
  field: SortField;
  direction: 'asc' | 'desc';
};

// Define the filter type
type SupplierFilter = {
  abbreviation: string | null;
  name: string | null;
  contact: string | null;
};

export default function EnhancedSuppliers() {
  const { canManageSystemSettings } = usePermissions();
  const queryClient = useQueryClient();
  
  // State for filters, sorting, and pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<SupplierFilter>({
    abbreviation: null,
    name: null,
    contact: null
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'supplier_name',
    direction: 'asc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  
  // State for modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch suppliers data
  const { data: suppliers = [], isLoading, error: fetchError } = useQuery({
    queryKey: ['suppliers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('suppliers')
        .select('*')
        .order('supplier_name', { ascending: true });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      return data as Supplier[];
    }
  });

  // Apply filters and search
  const filteredSuppliers = useMemo(() => {
    if (!suppliers) return [];

    return suppliers.filter(supplier => {
      // Search query filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch = 
          supplier.suppl_abrev.toLowerCase().includes(searchLower) ||
          supplier.supplier_name.toLowerCase().includes(searchLower) ||
          supplier.supplier_contact.toLowerCase().includes(searchLower);
        
        if (!matchesSearch) return false;
      }

      // Abbreviation filter
      if (filters.abbreviation && !supplier.suppl_abrev.toLowerCase().includes(filters.abbreviation.toLowerCase())) {
        return false;
      }

      // Name filter
      if (filters.name && !supplier.supplier_name.toLowerCase().includes(filters.name.toLowerCase())) {
        return false;
      }

      // Contact filter
      if (filters.contact && !supplier.supplier_contact.toLowerCase().includes(filters.contact.toLowerCase())) {
        return false;
      }

      return true;
    });
  }, [suppliers, searchQuery, filters]);

  // Apply sorting
  const sortedSuppliers = useMemo(() => {
    return [...filteredSuppliers].sort((a, b) => {
      const direction = sortConfig.direction === 'asc' ? 1 : -1;
      
      switch (sortConfig.field) {
        case 'suppl_abrev': 
          return direction * a.suppl_abrev.localeCompare(b.suppl_abrev);
        case 'supplier_name': 
          return direction * a.supplier_name.localeCompare(b.supplier_name);
        case 'supplier_contact': 
          return direction * a.supplier_contact.localeCompare(b.supplier_contact);
        default: 
          return 0;
      }
    });
  }, [filteredSuppliers, sortConfig]);

  // Apply pagination
  const paginatedSuppliers = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedSuppliers.slice(startIndex, endIndex);
  }, [sortedSuppliers, currentPage, pageSize]);

  // Handle sorting
  const handleSort = (sortConfig: { field: string; direction: 'asc' | 'desc' }) => {
    console.log('Sorting:', sortConfig);
    setSortConfig(sortConfig as SortConfig);
  };

  // Handle sort button clicks
  const handleSortButtonClick = (field: SortField) => {
    setSortConfig(current => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle filter changes
  const handleFilterChange = (newFilters: Partial<SupplierFilter>) => {
    setFilters(current => ({ ...current, ...newFilters }));
  };

  // Handle removing a filter
  const handleRemoveFilter = (key: keyof SupplierFilter) => {
    setFilters(prev => ({ ...prev, [key]: null }));
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Handle form submission
  const handleSubmit = async (data: Supplier) => {
    setError(null);
    setIsSubmitting(true);
    try {
      if (selectedSupplier) {
        // Update existing supplier
        const { error: updateError } = await supabase
          .from('suppliers')
          .update(data)
          .eq('suppl_abrev', selectedSupplier.suppl_abrev);
        
        if (updateError) throw updateError;
      } else {
        // Add new supplier
        const { error: insertError } = await supabase
          .from('suppliers')
          .insert(data);
        
        if (insertError) throw insertError;
      }
      
      await queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      setIsModalOpen(false);
      setSelectedSupplier(undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate pagination configuration
  const paginationConfig = {
    currentPage,
    pageSize,
    totalCount: sortedSuppliers.length,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    pageSizeOptions: [25, 50, 100, 200]
  };

  // Define table columns
  const columns: TableColumn<Supplier>[] = [
    {
      id: 'actions',
      header: 'Edit',
      accessor: (row) => (
        <div className="action-buttons">
          <button
            onClick={() => {
              setSelectedSupplier(row);
              setIsModalOpen(true);
            }}
            className="action-button edit"
            title="Edit Supplier"
          >
            <Pencil className="h-5 w-5" />
          </button>
        </div>
      ),
      width: 'extra-narrow' as ColumnWidth,
      // Only show the Edit column for users with admin privileges
      visibleWhen: (permissions) => permissions.isAdmin || permissions.canManageSystemSettings
    },
    {
      id: 'suppl_abrev',
      header: 'Abbreviation',
      accessor: (row) => (
        <div className="truncate" title={row.suppl_abrev}>
          {row.suppl_abrev}
        </div>
      ),
      width: 'narrow' as ColumnWidth,
      sortable: true
    },
    {
      id: 'supplier_name',
      header: 'Name',
      accessor: (row) => (
        <div className="truncate" title={row.supplier_name}>
          {row.supplier_name}
        </div>
      ),
      width: 'wide' as ColumnWidth,
      sortable: true
    },
    {
      id: 'supplier_contact',
      header: 'Contact',
      accessor: (row) => (
        <div className="truncate" title={row.supplier_contact}>
          {row.supplier_contact}
        </div>
      ),
      width: 'wide' as ColumnWidth,
      sortable: true
    }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800"></div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="p-4 text-red-500">
        Error loading supplier data. Please try again later.
      </div>
    );
  }

  return (
    <div className="enhanced-suppliers-container p-4">
      <div className="enhanced-suppliers-header">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold">Suppliers</h1>
            
            {/* Add button - only visible to users with edit permissions */}
            {canManageSystemSettings && (
              <button 
                onClick={() => {
                  setSelectedSupplier(undefined);
                  setIsModalOpen(true);
                }}
                className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Supplier
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Search input */}
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search suppliers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
            
            {/* Filter button */}
            <button
              className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              title="Toggle filters"
            >
              <Filter className="h-5 w-5 text-gray-500" />
              <span className="hidden sm:inline">Filters</span>
              {Object.values(filters).filter(Boolean).length > 0 && (
                <span className="ml-1 bg-primary text-white text-xs rounded-full px-2 py-0.5">
                  {Object.values(filters).filter(Boolean).length}
                </span>
              )}
            </button>
            
            <div className="text-base font-bold text-gray-700">
              Total Records: {filteredSuppliers.length}
            </div>
          </div>

          {/* Active filters */}
          {Object.values(filters).some(Boolean) && (
            <div className="active-filters">
              {filters.abbreviation && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Abbreviation:</span>
                  <span>{filters.abbreviation}</span>
                  <button
                    onClick={() => handleRemoveFilter('abbreviation')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              {filters.name && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Name:</span>
                  <span>{filters.name}</span>
                  <button
                    onClick={() => handleRemoveFilter('name')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              {filters.contact && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Contact:</span>
                  <span>{filters.contact}</span>
                  <button
                    onClick={() => handleRemoveFilter('contact')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Filter panel */}
          {isFilterOpen && (
            <div className="filter-panel">
              <div className="filter-panel-header">
                <h3 className="font-medium text-gray-700">Filters</h3>
                <button
                  onClick={() => setFilters({
                    abbreviation: null,
                    name: null,
                    contact: null
                  })}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Clear all
                </button>
              </div>
              <div className="filter-panel-body">
                {/* Abbreviation filter */}
                <div className="filter-group">
                  <label htmlFor="abbreviation-filter">Abbreviation</label>
                  <input
                    id="abbreviation-filter"
                    type="text"
                    value={filters.abbreviation || ''}
                    onChange={(e) => handleFilterChange({ abbreviation: e.target.value || null })}
                    placeholder="Filter by abbreviation"
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
                
                {/* Name filter */}
                <div className="filter-group">
                  <label htmlFor="name-filter">Name</label>
                  <input
                    id="name-filter"
                    type="text"
                    value={filters.name || ''}
                    onChange={(e) => handleFilterChange({ name: e.target.value || null })}
                    placeholder="Filter by name"
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
                
                {/* Contact filter */}
                <div className="filter-group">
                  <label htmlFor="contact-filter">Contact</label>
                  <input
                    id="contact-filter"
                    type="text"
                    value={filters.contact || ''}
                    onChange={(e) => handleFilterChange({ contact: e.target.value || null })}
                    placeholder="Filter by contact"
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Sort controls removed - now using column headers for sorting */}
        </div>
      </div>
      
      <div className="enhanced-suppliers-table-container enhanced-table-force-scroll">
        <EnhancedSuppliersTable
          columns={columns}
          data={paginatedSuppliers}
          keyField="suppl_abrev"
          width="1200px"
          maxHeight="calc(100vh - 20rem)"
          loading={isLoading}
          emptyMessage="No suppliers found"
          horizontalScroll={true}
          alternateRowColors={true}
          pagination={paginationConfig}
          defaultSort={sortConfig}
          onSort={handleSort}
          permissions={{
            isAdmin: usePermissions().isAdmin,
            canManageSystemSettings: canManageSystemSettings
          }}
        />
      </div>

      {/* Supplier Form Modal */}
      <SupplierFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedSupplier(undefined);
          setError(null);
        }}
        initialData={selectedSupplier}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        error={error}
        title={selectedSupplier ? 'Edit Supplier' : 'Add Supplier'}
        submitLabel={selectedSupplier ? 'Save Changes' : 'Add Supplier'}
      />
    </div>
  );
}
